/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { CubismViewMatrix } from '@framework/math/cubismviewmatrix';

import * as LAppDefine from './lappdefine';
import { LAppDelegate } from './lappdelegate';
import { LAppPal } from './lapppal';
import { LAppSprite } from './lappsprite';
import { TextureInfo } from './lapptexturemanager';
import { TouchManager } from './touchmanager';
import { LAppSubdelegate } from './lappsubdelegate';

/**
 * 描画クラス。
 */
export class LAppView {
  /**
   * コンストラクタ
   */
  public constructor() {
    this._programId = null;
    this._back = null;
    this._gear = null;

    // タッチ関係のイベント管理
    this._touchManager = new TouchManager();
    this._touchStartDeviceX = 0.0;
    this._touchStartDeviceY = 0.0;
    this._isDraggingGesture = false;
    this._isLive2DFollowing = false;

    // デバイス座標からスクリーン座標に変換するための
    this._deviceToScreen = new CubismMatrix44();

    // 画面の表示の拡大縮小や移動の変換を行う行列
    this._viewMatrix = new CubismViewMatrix();
    this._baseViewMatrix = new Float32Array(16);
    this._manualScale = 1.0;
    this._viewScaleListener = null;
    this._viewWheelListener = null;
    this._viewPanModeListener = null;
    this._lastPanDeviceX = 0.0;
    this._lastPanDeviceY = 0.0;
    this._isViewPanning = false;
    this._viewPanEnabled = false;
    this._touchStartedInPanMode = false;
  }

  /**
   * 初期化する。
   */
  public initialize(subdelegate: LAppSubdelegate): void {
    this._subdelegate = subdelegate;
    const { width, height } = subdelegate.getCanvas();

    const ratio: number = width / height;
    const left: number = -ratio;
    const right: number = ratio;
    const bottom: number = LAppDefine.ViewLogicalLeft;
    const top: number = LAppDefine.ViewLogicalRight;

    this._viewMatrix.setScreenRect(left, right, bottom, top); // デバイスに対応する画面の範囲。 Xの左端、Xの右端、Yの下端、Yの上端
    this._viewMatrix.scale(LAppDefine.ViewScale, LAppDefine.ViewScale);

    this._deviceToScreen.loadIdentity();
    if (width > height) {
      const screenW: number = Math.abs(right - left);
      this._deviceToScreen.scaleRelative(screenW / width, -screenW / width);
    } else {
      const screenH: number = Math.abs(top - bottom);
      this._deviceToScreen.scaleRelative(screenH / height, -screenH / height);
    }
    this._deviceToScreen.translateRelative(-width * 0.5, -height * 0.5);

    // 表示範囲の設定
    this._viewMatrix.setMaxScale(LAppDefine.ViewMaxScale); // 限界拡張率
    this._viewMatrix.setMinScale(LAppDefine.ViewMinScale); // 限界縮小率

    // 表示できる最大範囲
    this._viewMatrix.setMaxScreenRect(
      LAppDefine.ViewLogicalMaxLeft,
      LAppDefine.ViewLogicalMaxRight,
      LAppDefine.ViewLogicalMaxBottom,
      LAppDefine.ViewLogicalMaxTop
    );
    this.storeBaseViewMatrix();
    this.applyManualScale();

    if (!this._viewScaleListener) {
      this._viewScaleListener = (event: Event): void => {
        const detail = (event as CustomEvent<{ scale?: number }>).detail;
        this.setManualScale(Number(detail?.scale));
      };
      window.addEventListener('live2d-view-scale-changed', this._viewScaleListener);
    }

    if (!this._viewWheelListener) {
      this._viewWheelListener = (event: WheelEvent): void => this.onViewWheel(event);
      window.addEventListener('wheel', this._viewWheelListener, { passive: false });
    }

    if (!this._viewPanModeListener) {
      this._viewPanModeListener = (event: Event): void => {
        const detail = (event as CustomEvent<{ panEnabled?: boolean }>).detail;
        this._viewPanEnabled = detail?.panEnabled === true;
        if (!this._viewPanEnabled && this._isViewPanning) {
          this._isViewPanning = false;
          document.body.classList.remove('view-panning');
        }
      };
      window.addEventListener('live2d-view-pan-mode-changed', this._viewPanModeListener);
    }
  }

  /**
   * 解放する
   */
  public release(): void {
    if (this._viewScaleListener) {
      window.removeEventListener('live2d-view-scale-changed', this._viewScaleListener);
      this._viewScaleListener = null;
    }
    if (this._viewWheelListener) {
      window.removeEventListener('wheel', this._viewWheelListener);
      this._viewWheelListener = null;
    }
    if (this._viewPanModeListener) {
      window.removeEventListener('live2d-view-pan-mode-changed', this._viewPanModeListener);
      this._viewPanModeListener = null;
    }
    document.body.classList.remove('view-panning');

    this._viewMatrix = null;
    this._touchManager = null;
    this._deviceToScreen = null;

    this._gear?.release();
    this._gear = null;

    this._back?.release();
    this._back = null;

    this._subdelegate.getGlManager().getGl().deleteProgram(this._programId);
    this._programId = null;
  }

  /**
   * 描画する。
   */
  public render(): void {
    this._subdelegate.getGlManager().getGl().useProgram(this._programId);

    if (this._back) {
      this._back.render(this._programId);
    }
    if (this._gear) {
      this._gear.render(this._programId);
    }

    this._subdelegate.getGlManager().getGl().flush();

    const lapplive2dmanager = this._subdelegate.getLive2DManager();
    if (lapplive2dmanager != null) {
      lapplive2dmanager.setViewMatrix(this._viewMatrix);

      lapplive2dmanager.onUpdate();
    }
  }

  /**
   * 画像の初期化を行う。
   */
  private onViewWheel(event: WheelEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('#motion-panel')) {
      return;
    }

    const canvas = this._subdelegate?.getCanvas?.();
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      return;
    }

    event.preventDefault();
    this.zoomViewAt(event.clientX - rect.left, event.clientY - rect.top, event.deltaY, event.deltaMode);
  }

  private zoomViewAt(pointX: number, pointY: number, deltaY: number, deltaMode: number): void {
    let normalizedDelta = deltaY;
    if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
      normalizedDelta *= 16;
    } else if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      normalizedDelta *= 120;
    }

    const scale = Math.exp(-normalizedDelta * 0.0015);
    const screenX = this.transformScreenX(pointX * window.devicePixelRatio);
    const screenY = this.transformScreenY(pointY * window.devicePixelRatio);
    this._viewMatrix.adjustScale(screenX, screenY, scale);
    this._manualScale = this.currentManualScale();
    this.notifyViewScaleChanged();
  }

  private currentManualScale(): number {
    const baseScale = this._baseViewMatrix?.[0] || LAppDefine.ViewScale || 1.0;
    const currentScale = this._viewMatrix?.getScaleX?.() || baseScale;
    return Math.max(0.6, Math.min(3.0, currentScale / baseScale));
  }

  private notifyViewScaleChanged(): void {
    window.dispatchEvent(new CustomEvent('live2d-view-scale-applied', { detail: { scale: this.currentManualScale() } }));
  }

  public setManualScale(scale: number): void {
    this._manualScale = Number.isFinite(scale)
      ? Math.max(0.6, Math.min(3.0, scale))
      : 1.0;
    this.applyManualScale();
    this.notifyViewScaleChanged();
  }

  private storeBaseViewMatrix(): void {
    const source = this._viewMatrix.getArray();
    if (!this._baseViewMatrix || this._baseViewMatrix.length !== source.length) {
      this._baseViewMatrix = new Float32Array(source.length);
    }
    this._baseViewMatrix.set(source);
  }

  private restoreBaseViewMatrix(): void {
    const target = this._viewMatrix.getArray();
    for (let index = 0; index < this._baseViewMatrix.length; index += 1) {
      target[index] = this._baseViewMatrix[index];
    }
  }

  private applyManualScale(): void {
    if (!this._viewMatrix || !this._baseViewMatrix) {
      return;
    }

    this.restoreBaseViewMatrix();
    this._viewMatrix.adjustScale(0, 0, this._manualScale);
  }

  public initializeSprite(): void {
    this._back = null;
    this._gear = null;

    if (this._programId == null) {
      this._programId = this._subdelegate.createShader();
    }
  }


  /**
   * タッチされた時に呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesBegan(pointX: number, pointY: number): void {
    const posX = pointX * window.devicePixelRatio;
    const posY = pointY * window.devicePixelRatio;

    this._touchStartDeviceX = posX;
    this._touchStartDeviceY = posY;
    this._lastPanDeviceX = posX;
    this._lastPanDeviceY = posY;
    this._isDraggingGesture = false;
    this._isLive2DFollowing = false;
    this._isViewPanning = false;
    this._touchStartedInPanMode = this._viewPanEnabled;
    document.body.classList.remove('view-panning');

    this._touchManager.touchesBegan(posX, posY);
  }

  /**
   * タッチしているときにポインタが動いたら呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesMoved(pointX: number, pointY: number): void {
    const posX = pointX * window.devicePixelRatio;
    const posY = pointY * window.devicePixelRatio;
    const deltaX = posX - this._touchStartDeviceX;
    const deltaY = posY - this._touchStartDeviceY;
    const panGestureActive =
      this._touchStartedInPanMode || this._viewPanEnabled;
    const live2dManager = this._subdelegate.getLive2DManager();

    if (
      !this._isDraggingGesture &&
      Math.hypot(deltaX, deltaY) > 8 * window.devicePixelRatio
    ) {
      this._isDraggingGesture = true;
      if (panGestureActive) {
        this._touchStartedInPanMode = true;
        this._isViewPanning = true;
        document.body.classList.add('view-panning');
      } else {
        const startX = this.transformViewX(this._touchStartDeviceX);
        const startY = this.transformViewY(this._touchStartDeviceY);
        this._isLive2DFollowing = live2dManager.onDragBegin(
          startX,
          startY
        );
      }
    }

    if (panGestureActive && this._isViewPanning) {
      const currentScreenX = this.transformScreenX(posX);
      const currentScreenY = this.transformScreenY(posY);
      const lastScreenX = this.transformScreenX(this._lastPanDeviceX);
      const lastScreenY = this.transformScreenY(this._lastPanDeviceY);
      this._viewMatrix.adjustTranslate(
        currentScreenX - lastScreenX,
        currentScreenY - lastScreenY
      );
      this._lastPanDeviceX = posX;
      this._lastPanDeviceY = posY;
      return;
    }

    this._lastPanDeviceX = posX;
    this._lastPanDeviceY = posY;
    this._touchManager.touchesMoved(posX, posY);
    if (!this._isLive2DFollowing) {
      return;
    }

    const viewX: number = this.transformViewX(this._touchManager.getX());
    const viewY: number = this.transformViewY(this._touchManager.getY());
    live2dManager.onDragMove(viewX, viewY);
  }

  /**
   * タッチが終了したら呼ばれる。
   *
   * @param pointX スクリーンX座標
   * @param pointY スクリーンY座標
   */
  public onTouchesEnded(pointX: number, pointY: number): void {
    const posX = pointX * window.devicePixelRatio;
    const posY = pointY * window.devicePixelRatio;

    const lapplive2dmanager = this._subdelegate.getLive2DManager();
    const wasDraggingGesture = this._isDraggingGesture;
    const wasViewPanning = this._isViewPanning;
    const wasPanGesture = this._touchStartedInPanMode || wasViewPanning || this._viewPanEnabled;
    this._isViewPanning = false;
    this._touchStartedInPanMode = false;
    document.body.classList.remove('view-panning');

    if (this._isLive2DFollowing) {
      lapplive2dmanager.onDragEnd();
    }
    this._isLive2DFollowing = false;

    const x: number = this.transformViewX(posX);
    const y: number = this.transformViewY(posY);

    if (!wasDraggingGesture && !wasPanGesture) {
      if (LAppDefine.DebugTouchLogEnable) {
        LAppPal.printMessage(`[APP]touchesEnded x: ${x} y: ${y}`);
      }
      lapplive2dmanager.onTap(x, y);
    } else if (LAppDefine.DebugTouchLogEnable) {
      LAppPal.printMessage(`[APP]drag ended without tap x: ${x} y: ${y}`);
    }
    this._isDraggingGesture = false;

    if (!wasDraggingGesture && !wasPanGesture && this._gear && this._gear.isHit(posX, posY)) {
      lapplive2dmanager.nextScene();
    }
  }

  /**
   * X座標をView座標に変換する。
   *
   * @param deviceX デバイスX座標
   */
  public transformViewX(deviceX: number): number {
    const screenX: number = this._deviceToScreen.transformX(deviceX); // 論理座標変換した座標を取得。
    return this._viewMatrix.invertTransformX(screenX); // 拡大、縮小、移動後の値。
  }

  /**
   * Y座標をView座標に変換する。
   *
   * @param deviceY デバイスY座標
   */
  public transformViewY(deviceY: number): number {
    const screenY: number = this._deviceToScreen.transformY(deviceY); // 論理座標変換した座標を取得。
    return this._viewMatrix.invertTransformY(screenY);
  }

  /**
   * X座標をScreen座標に変換する。
   * @param deviceX デバイスX座標
   */
  public transformScreenX(deviceX: number): number {
    return this._deviceToScreen.transformX(deviceX);
  }

  /**
   * Y座標をScreen座標に変換する。
   *
   * @param deviceY デバイスY座標
   */
  public transformScreenY(deviceY: number): number {
    return this._deviceToScreen.transformY(deviceY);
  }

  _touchManager: TouchManager; // タッチマネージャー
  _deviceToScreen: CubismMatrix44; // デバイスからスクリーンへの行列
  _viewMatrix: CubismViewMatrix; // viewMatrix
  _baseViewMatrix: Float32Array;
  _manualScale: number;
  private _viewScaleListener: ((event: Event) => void) | null;
  private _viewWheelListener: ((event: WheelEvent) => void) | null;
  private _viewPanModeListener: ((event: Event) => void) | null;
  _programId: WebGLProgram; // シェーダID
  _back: LAppSprite; // 背景画像
  _gear: LAppSprite; // ギア画像
  _changeModel: boolean; // モデル切り替えフラグ
  _isClick: boolean; // クリック中
  _touchStartDeviceX: number;
  _touchStartDeviceY: number;
  _isDraggingGesture: boolean;
  _isLive2DFollowing: boolean;
  _lastPanDeviceX: number;
  _lastPanDeviceY: number;
  _isViewPanning: boolean;
  _viewPanEnabled: boolean;
  _touchStartedInPanMode: boolean;
  private _subdelegate: LAppSubdelegate;
}
